import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { patientReminderService } from '../services/patientReminderService'
import type { ReminderRule, ReminderRuleFormData, ReminderDeliveryMethod } from '../types/patientReminder'
import { Modal } from '../components/Modal'
import './SettingsReminders.css'

const defaultFormState: ReminderRuleFormData = {
  name: '',
  description: '',
  trigger: {
    type: 'date',
    date: '',
  },
  message: '',
  deliveryMethod: 'sms',
  frequency: 'once',
  enabled: true,
}

export function SettingsReminders() {
  const { hasPermission } = useAuth()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState<ReminderRuleFormData>({ ...defaultFormState })

  if (!hasPermission('reminder-rules:read') && !hasPermission('*')) {
    return (
      <div className="settings-content" role="main">
        <div className="error" role="alert">
          <h2>Access Denied</h2>
          <p>You don't have permission to manage reminder rules.</p>
        </div>
      </div>
    )
  }

  const { data: rules = [], isLoading, error } = useQuery<ReminderRule[]>({
    queryKey: ['reminder-rules'],
    queryFn: () => patientReminderService.getReminderRules(),
  })

  const createMutation = useMutation({
    mutationFn: (data: ReminderRuleFormData) => patientReminderService.createRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminder-rules'] })
      setShowForm(false)
      setFormData({ ...defaultFormState })
    },
  })

  const handleFormChange = (field: keyof ReminderRuleFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleTriggerChange = (field: keyof ReminderRuleFormData['trigger'], value: any) => {
    setFormData((prev) => ({
      ...prev,
      trigger: {
        ...prev.trigger,
        [field]: value,
      },
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await createMutation.mutateAsync(formData)
  }

  return (
    <div className="settings-content settings-reminders" role="main">
      <div className="settings-header">
        <div>
          <h1>Reminder Rules</h1>
          <p className="settings-subtitle">Define templates and schedules for patient recalls and reminders.</p>
        </div>
        {hasPermission('reminder-rules:write') && (
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            + Add Rule
          </button>
        )}
      </div>

      {isLoading && <div className="loading" role="status">Loading reminder rules...</div>}
      {error && <div className="error" role="alert">Failed to load reminder rules.</div>}

      {!isLoading && !error && (
        <div className="reminder-rules-list">
          {rules.length === 0 ? (
            <div className="empty-state">
              <p>No reminder rules configured yet.</p>
              {hasPermission('reminder-rules:write') && (
                <button className="btn-primary" onClick={() => setShowForm(true)}>
                  Create First Rule
                </button>
              )}
            </div>
          ) : (
            <table className="rules-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Trigger</th>
                  <th>Delivery</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id}>
                    <td>
                      <div className="rule-name">{rule.name}</div>
                      {rule.description && <div className="rule-description">{rule.description}</div>}
                    </td>
                    <td>
                      <span className="trigger-badge">{rule.trigger.type}</span>
                    </td>
                    <td>
                      <span className="delivery-badge">{rule.deliveryMethod}</span>
                    </td>
                    <td>{rule.enabled ? 'Enabled' : 'Disabled'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showForm && (
        <Modal
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          title="Create Reminder Rule"
          size="large"
        >
          <form onSubmit={handleSubmit} className="reminder-rule-form">
            <div className="form-group">
              <label htmlFor="rule-name">Rule Name</label>
              <input
                id="rule-name"
                type="text"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="rule-description">Description</label>
              <input
                id="rule-description"
                type="text"
                value={formData.description ?? ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="rule-message">Message</label>
              <textarea
                id="rule-message"
                value={formData.message}
                onChange={(e) => handleFormChange('message', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="rule-delivery">Delivery Method</label>
              <select
                id="rule-delivery"
                value={formData.deliveryMethod}
                onChange={(e) => handleFormChange('deliveryMethod', e.target.value as ReminderDeliveryMethod)}
              >
                <option value="sms">SMS</option>
                <option value="email">Email</option>
                <option value="in-app">In-app</option>
                <option value="all">All</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="rule-trigger">Trigger Type</label>
              <select
                id="rule-trigger"
                value={formData.trigger.type}
                onChange={(e) => handleTriggerChange('type', e.target.value)}
              >
                <option value="date">Date</option>
                <option value="appointment">Appointment</option>
                <option value="medication">Medication</option>
                <option value="condition">Condition</option>
                <option value="task">Task</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            {formData.trigger.type === 'date' && (
              <div className="form-group">
                <label htmlFor="rule-date">Date</label>
                <input
                  id="rule-date"
                  type="date"
                  value={formData.trigger.date || ''}
                  onChange={(e) => handleTriggerChange('date', e.target.value)}
                />
              </div>
            )}
            <div className="form-group">
              <label htmlFor="rule-frequency">Frequency</label>
              <select
                id="rule-frequency"
                value={formData.frequency || 'once'}
                onChange={(e) => handleFormChange('frequency', e.target.value)}
              >
                <option value="once">Once</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Saving...' : 'Save Rule'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}
